/* global t, runtime, mergeState, safeArray, num, getReportsModel, buildLedgerLines, buildVatFilingModel, deriveVat, vatReconRound, vatReconDuplicateKey, vatReconSalesRows, vatReconPurchaseRows, vatReconCustomsIndex */
// MVA-AVSTEMMING — lesemodell for byråets MVA-skjerm
//
// MVA-skjermen i avstemmingsmappa trenger noe den generiske kontoflaten ikke
// har: hva saldoen på 2700 og 2710 BESTÅR AV. En saldo på MVA-kontoene sier
// bare hva noen har ført dit; om avgiften er riktig beregnet avgjøres av
// transaksjonene bak, av avgiftsbehandlingen på hver enkelt av dem, og av om
// terminens melding er levert.
//
// Modellen er READ-ONLY, og den regner ikke ut avgiften selv. Grunnlaget og
// kodene kommer fra `deriveVat`, terminen og meldingsstatusen fra
// `buildVatFilingModel`, hovedbokstallene fra `buildLedgerLines` og
// saldobalansen. To steder å regne ut den samme avgiften ville gitt to svar, og
// avstemmingen ville avstemt mot seg selv.
//
// Transaksjonene bak avgiften — salgs- og kjøpslinjene med varslene dataene gir
// grunnlag for — ligger i backend-vat-recon-items.jsx. Denne fila eier
// hovedboken og sammenstillingen.
//
// Den finner ikke opp varsler. Hvert flagg peker på noe dataene faktisk sier.
// Designets `uvanlig_kode` (uvanlig MVA-kode for konto) er IKKE portert: den
// forutsetter en forventet MVA-kode per konto, og kontoplanen i appen har ingen.
// Et varsel uten kilde er verre enn ingen varsel.

// Kontrollkontoene for MVA. Appen fører all utgående MVA på 2700 og all
// inngående på 2710 uansett sats. En importert kontoplan kan likevel ha delt
// dem per sats (2701, 2702, 2711 …), og da skal kodene sammenliknes med SUMMEN
// av kontoene på hver side — ikke med én av dem. Sammenliknet vi bare 2700,
// ville en klient med delte kontoer fått halve avgiften til å se ut som et avvik.
//
// Øvrige 27xx (oppgjørskontoen 2740, import 2749) tas med når de har saldo eller
// bevegelse, slik at en oppgjørskonto som ikke er nullstilt blir synlig.
const VAT_RECON_ACCOUNT_PREFIX = '27';
const VAT_RECON_OUTPUT_ACCOUNT = '2700';
const VAT_RECON_INPUT_ACCOUNT = '2710';
const vatReconAccountSide = (code) => {
  if (/^270\d$/.test(code)) return 'utgaaende';
  if (/^271\d$/.test(code)) return 'inngaaende';
  return 'annet';
};

const vatReconMonthRange = (periodId) => {
  const [yearText, monthText] = String(periodId || '').split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) return null;
  const pad = (value) => String(value).padStart(2, '0');
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return {
    year,
    monthIndex,
    from: year + '-' + pad(monthIndex + 1) + '-01',
    to: year + '-' + pad(monthIndex + 1) + '-' + pad(lastDay),
  };
};

// MVA avstemmes per TERMIN, ikke per måned. Terminen kommer fra
// `buildVatFilingModel`, som er den samme modellen innleveringssiden i
// klientappen bruker — regnet avstemmingen ut sin egen terminlengde, kunne de to
// vært uenige om hvilken periode meldingen dekker.
//
// Faller måneden utenfor enhver termin — et år uten MVA-modell — avstemmes
// måneden alene. Det er en dårligere avstemming, men en ærlig en: den sier hva
// den faktisk sammenlikner.
const vatReconTermRange = ({ range, term }) => {
  if (!term || !Array.isArray(term.months)) {
    return { ...range, monthStart: range.monthIndex, monthEnd: range.monthIndex, isTerm: false };
  }
  return {
    year: range.year,
    monthIndex: range.monthIndex,
    monthStart: term.months[0],
    monthEnd: term.months[1],
    from: term.dateFrom,
    to: term.dateTo,
    isTerm: true,
  };
};

// ------------------------------------------------------------------
// MVA-kontoene i hovedboken
// ------------------------------------------------------------------

const vatReconLedgerAccounts = ({ state, range, codes }) => {
  const reports = getReportsModel({
    year: range.year, months: [range.monthStart, range.monthEnd], includeChart: false, includeResult: false,
  });
  const trial = new Map();
  for (const group of safeArray(reports?.trial)) {
    for (const account of safeArray(group.accounts)) {
      const code = String(account.nr || '');
      if (code.startsWith(VAT_RECON_ACCOUNT_PREFIX)) trial.set(code, account);
    }
  }
  const accountCodes = [...trial.keys()];
  const lines = accountCodes.length
    ? buildLedgerLines({ from: range.from, to: range.to, accountCodes, state })
    : [];
  const movement = new Map();
  for (const line of lines) {
    const entry = movement.get(line.accountCode) || { debet: 0, kredit: 0 };
    if (num(line.amount) >= 0) entry.debet += num(line.amount);
    else entry.kredit += -num(line.amount);
    movement.set(line.accountCode, entry);
  }

  const rows = [...trial.entries()]
    .map(([code, account]) => {
      const side = vatReconAccountSide(code);
      const entry = movement.get(code) || { debet: 0, kredit: 0 };
      return {
        code,
        name: account.navn || account.name || '',
        side,
        opening: vatReconRound(account.inn),
        debet: vatReconRound(entry.debet),
        kredit: vatReconRound(entry.kredit),
        closing: vatReconRound(account.ut),
        // Utgående MVA står på kreditsiden, inngående på debetsiden. Det er den
        // siden kodene kan sammenliknes med — motposten er salget eller kjøpet,
        // ikke avgiften.
        booked: side === 'utgaaende' ? vatReconRound(entry.kredit - entry.debet)
          : side === 'inngaaende' ? vatReconRound(entry.debet - entry.kredit)
            : null,
        // En oppgjørskonto med saldo inn i perioden er forrige termins oppgjør
        // som ikke er gjort opp. Det er et faktum fra saldobalansen, ikke en
        // vurdering. 2740 skal fortsatt stemme med den leverte meldingen.
        carriedOver: side === 'annet' && Math.abs(num(account.inn)) > 0.005,
      };
    })
    .filter((row) => row.opening || row.closing || row.debet || row.kredit)
    .sort((left, right) => left.code.localeCompare(right.code));

  // Kodene sier hva avgiften SKAL være. Sammenlikningen gjøres mot SUMMEN av
  // kontoene på hver side, slik at en kontoplan med én konto per sats får det
  // samme svaret som appens egen med 2700 og 2710 alene.
  const sideOf = (name) => {
    const accounts = rows.filter((row) => row.side === name);
    const booked = vatReconRound(accounts.reduce((sum, row) => sum + num(row.booked), 0));
    const expected = vatReconRound(name === 'utgaaende'
      ? codes.reduce((sum, code) => sum + (code.outgoing === undefined ? num(code.mva) : num(code.outgoing)), 0)
      : codes.reduce((sum, code) => sum + (code.incoming === undefined ? -num(code.mva) : num(code.incoming)), 0));
    return {
      accounts: accounts.map((row) => row.code),
      booked: accounts.length ? booked : null,
      expected,
      difference: accounts.length ? vatReconRound(booked - expected) : null,
    };
  };
  const sides = { utgaaende: sideOf('utgaaende'), inngaaende: sideOf('inngaaende') };

  // Er det bare én konto på siden — det vanlige — bærer raden selv
  // sammenlikningen. Er de flere, hører den hjemme på sumraden: 2701 alene skal
  // ikke se ut som et avvik fordi den ikke er hele avgiften.
  for (const row of rows) {
    const group = sides[row.side];
    const single = group && group.accounts.length === 1;
    row.expected = single ? group.expected : null;
    row.difference = single ? group.difference : null;
  }
  return { rows, lines, sides };
};


// ------------------------------------------------------------------
// Hele MVA-bildet for én periode
// ------------------------------------------------------------------

const vatReconChecks = ({ state, counts, sides, term }) => [
  { key: 'registrert', label: t('rcv.check.registered', 'Virksomheten er MVA-registrert for perioden'), value: state.settings?.vatRegistered === false ? 1 : 0 },
  { key: 'kode', label: t('rcv.check.treatment', 'Alle transaksjoner har fastsatt avgiftsbehandling'), value: counts.manglerKode },
  { key: 'utgaaende', label: t('rcv.check.outgoing', 'Utgående MVA i hovedboken stemmer med MVA-kodene'), value: Math.abs(sides.utgaaende.difference || 0) > 0.5 ? 1 : 0 },
  { key: 'inngaaende', label: t('rcv.check.incoming', 'Inngående MVA i hovedboken stemmer med MVA-kodene'), value: Math.abs(sides.inngaaende.difference || 0) > 0.5 ? 1 : 0 },
  { key: 'direkte', label: t('rcv.check.direct', 'Direkteføringer på MVA-kontoene er identifisert'), value: counts.direkte },
  { key: 'duplikat', label: t('rcv.check.duplicates', 'Mulige duplikater er identifisert'), value: counts.duplikat },
  { key: 'fradrag', label: t('rcv.check.deduction', 'Kjøp med begrenset fradragsrett er identifisert'), value: counts.ingenFradrag + counts.delvisFradrag },
  { key: 'omvendt', label: t('rcv.check.reverse', 'Omvendt avgiftsplikt er identifisert'), value: counts.omvendt },
  { key: 'import', label: t('rcv.check.import', 'Vareimport har tolldeklarasjon'), value: counts.tolldokMangler },
  { key: 'uten_mva', label: t('rcv.check.exempt', 'Salg uten MVA er identifisert'), value: counts.utenMvaDok },
  { key: 'kreditnota', label: t('rcv.check.credit', 'Kreditnotaer i perioden er identifisert'), value: counts.kreditnota },
  { key: 'oppgjor', label: t('rcv.check.settlement', 'Oppgjørskontoen er nullstilt fra forrige termin'), value: counts.carriedOver },
  { key: 'melding', label: t('rcv.check.filed', 'MVA-meldingen for terminen er levert'), value: term && term.status !== 'levert' && term.status !== 'levert-betalt' ? 1 : 0 },
];

// Datakildene skjermen faktisk leser. Hver rad er en kilde som finnes — eller
// mangler — i appen, ikke en liste over hva et regnskapssystem kunne hatt.
const vatReconSources = ({ items, codes, ledger, customsCount, counts, term }) => [
  { key: 'hovedbok', label: t('rcv.src.ledger', 'Hovedbok'), complete: ledger.rows.length > 0, detail: t('rcv.src.ledgerDetail', '{count} posteringer på MVA-kontoene i perioden', { count: ledger.lines.length }) },
  { key: 'salg', label: t('rcv.src.sales', 'Bokførte salgsbilag'), complete: true, detail: t('rcv.src.linesDetail', '{count} linjer i perioden', { count: items.filter((row) => row.domain === 'salg').length }) },
  { key: 'kjop', label: t('rcv.src.purchases', 'Bokførte kjøpsbilag'), complete: true, detail: t('rcv.src.linesDetail', '{count} linjer i perioden', { count: items.filter((row) => row.domain !== 'salg').length }) },
  { key: 'spesifikasjon', label: t('rcv.src.spec', 'MVA-spesifikasjon'), complete: codes.length > 0, detail: t('rcv.src.specDetail', '{count} MVA-koder med beløp', { count: codes.length }) },
  { key: 'toll', label: t('rcv.src.customs', 'Tolldeklarasjoner'), complete: counts.tolldokMangler === 0, detail: t('rcv.src.customsDetail', '{registered} registrert · {missing} mangler', { registered: customsCount, missing: counts.tolldokMangler }) },
  { key: 'melding', label: t('rcv.src.filing', 'MVA-melding for terminen'), complete: Boolean(term && (term.status === 'levert' || term.status === 'levert-betalt')), detail: term ? t('rcv.src.filingDetail', '{period} · frist {due}', { period: term.period, due: term.frist }) : t('rcv.src.noTerm', 'Ingen termin funnet') },
  { key: 'betaling', label: t('rcv.src.payment', 'Betaling og oppgjør'), complete: Boolean(term && term.status === 'levert-betalt'), detail: term ? t('rcv.src.paymentDetail', '{count} betaling(er) registrert', { count: term.payments.length }) : '—' },
];

const buildVatReconciliationView = ({ periodId } = {}) => {
  const month = vatReconMonthRange(periodId);
  if (!month) {
    return {
      periodId, from: '', to: '', registered: false, term: null, termDue: false,
      summary: null, draft: null, ledgerSides: null,
      codes: [], items: [], ledgerAccounts: [], ledgerLines: [], mainRows: [],
      sources: [], counts: {}, checks: [],
    };
  }
  const state = mergeState(runtime.appState);

  // Terminen meldingen gjelder. Den kommer fra `buildVatFilingModel`, som er den
  // samme modellen innleveringssiden i klientappen bruker.
  const filing = buildVatFilingModel({ year: month.year });
  const term = safeArray(filing?.terms).find(
    (entry) => month.monthIndex >= entry.months[0] && month.monthIndex <= entry.months[1],
  ) || null;
  // Terminen er over i den SISTE måneden den dekker. Det er da meldingen skal
  // gjøres klar, og det er den måneden MVA-avstemmingen hører hjemme i. Samme
  // regel som backend bruker til fremdriften (functions/accounting/vatTerms.ts).
  const termDue = term ? month.monthIndex === term.months[1] : true;
  const range = vatReconTermRange({ range: month, term });

  const vat = deriveVat(state, range.year, { months: [range.monthStart, range.monthEnd] });
  const codes = safeArray(vat?.codes).filter(Boolean);

  const customsIndex = vatReconCustomsIndex(state, range);
  const rows = [...vatReconSalesRows(state, range), ...vatReconPurchaseRows(state, range, customsIndex)];
  const duplicates = new Map();
  for (const row of rows) {
    if (row.grunnlag === null) continue;
    const key = vatReconDuplicateKey(row);
    duplicates.set(key, (duplicates.get(key) || 0) + 1);
  }
  const items = rows.map((row) => (row.grunnlag !== null && duplicates.get(vatReconDuplicateKey(row)) > 1
    ? { ...row, flags: [...row.flags, 'duplikat'] }
    : row));

  const ledger = vatReconLedgerAccounts({ state, range, codes });

  const outgoing = vatReconRound(codes.reduce((sum, code) => sum + (code.outgoing === undefined ? num(code.mva) : num(code.outgoing)), 0));
  const incoming = vatReconRound(codes.reduce((sum, code) => sum + (code.incoming === undefined ? -num(code.mva) : num(code.incoming)), 0));
  const salesBase = vatReconRound(items.filter((row) => row.domain === 'salg')
    .reduce((sum, row) => sum + num(row.grunnlag), 0));

  const mainRow = (key, label, fromCodes, fromLedger, note) => ({
    key, label, codes: fromCodes, ledger: fromLedger, note: note || '',
    difference: fromCodes === null || fromLedger === null ? null : vatReconRound(fromLedger - fromCodes),
  });
  const bookedNet = ledger.sides.utgaaende.booked === null || ledger.sides.inngaaende.booked === null
    ? null
    : vatReconRound(ledger.sides.utgaaende.booked - ledger.sides.inngaaende.booked);
  const mainRows = [
    mainRow('omsetning', t('avf.tbl.sales.totalBasis', 'Samlet salgsgrunnlag'), salesBase, null,
      t('rcv.main.salesBasisNote', 'Grunnlaget ligger på inntektskontoene, ikke på en MVA-konto.')),
    mainRow('utgaaende', t('avf.tbl.sales.outputVat', 'Utgående MVA'), outgoing, ledger.sides.utgaaende.booked),
    mainRow('inngaaende', t('avf.sec.foreign.deductibleInput', 'Fradragsberettiget inngående MVA'), incoming, ledger.sides.inngaaende.booked),
    mainRow('netto', t('rcv.main.net', 'Netto MVA for terminen'), vatReconRound(outgoing - incoming), bookedNet),
  ];

  // Oppsummering av MVA-meldingen (utkast). Tallene er terminens egne, hentet fra
  // de samme kodene meldingen bygges av — ikke en ny beregning. Import og omvendt
  // avgiftsplikt står for seg fordi de har utgående OG inngående side i samme
  // transaksjon: en sum som skjuler det ser ut som en dobbel avgift.
  const VAT_RECON_IMPORT_CODES = ['81', '83', '84', '85', '86'];
  const draft = {
    outgoing,
    deductible: incoming,
    importAndReverseCharge: vatReconRound(codes
      .filter((code) => VAT_RECON_IMPORT_CODES.includes(String(code.code)))
      .reduce((sum, code) => sum + Math.abs(num(code.mva)), 0)),
    importIncomplete: items.some((row) => row.flags.includes('tolldok_mangler')),
    net: vatReconRound(outgoing - incoming),
    // Terminens eget nettotall fra MVA-modellen. Avviker det fra kodene her, er
    // det den samme forskjellen innleveringssiden ville vist — ikke en ny.
    termNet: term ? vatReconRound(term.netVat) : null,
    submitted: Boolean(term && (term.status === 'levert' || term.status === 'levert-betalt')),
    direction: outgoing - incoming < 0 ? 'tilgode' : 'betale',
  };

  const has = (flag) => items.filter((row) => row.flags.includes(flag)).length;
  const counts = {
    total: items.length,
    flagged: items.filter((row) => row.flags.length).length,
    manglerKode: has('mangler_kode'),
    ingenFradrag: has('ingen_fradrag'),
    delvisFradrag: has('delvis_fradrag'),
    omvendt: has('omvendt'),
    tolldokMangler: has('tolldok_mangler'),
    direkte: has('direkte'),
    duplikat: has('duplikat'),
    utenMvaDok: has('uten_mva_dok'),
    kreditnota: has('kreditnota'),
    carriedOver: ledger.rows.filter((row) => row.carriedOver).length,
  };

  return {
    periodId,
    from: range.from,
    to: range.to,
    registered: state.settings?.vatRegistered !== false,
    organizationNumber: state.settings?.organizationNumber || '',
    vatPeriod: window.PlummeVatPeriodHistory?.forDate?.(state.settings, range.from) || state.settings?.vatPeriod || 'bimonthly',
    term,
    // Er terminen over i denne måneden? Er den ikke det, er MVA-arbeidet ikke
    // forfalt — og området teller ikke mot fremdriften (backend avgjør det samme).
    termDue,
    summary: { outgoing, incoming, net: vatReconRound(outgoing - incoming), salesBase },
    draft,
    codes,
    items,
    ledgerAccounts: ledger.rows,
    ledgerSides: ledger.sides,
    ledgerLines: ledger.lines,
    mainRows,
    sources: vatReconSources({ items, codes, ledger, customsCount: customsIndex.size, counts, term }),
    counts,
    checks: vatReconChecks({ state, counts, sides: ledger.sides, term }),
  };
};

Object.assign(window, {
  buildVatReconciliationView, vatReconMonthRange, vatReconTermRange, vatReconAccountSide,
  VAT_RECON_OUTPUT_ACCOUNT, VAT_RECON_INPUT_ACCOUNT,
});
